/**
 * DocumentViewerPanel Component
 * TASK-373: AI-Assisted KYC Filling Workspace
 *
 * Left upper panel for viewing uploaded client documents.
 * Includes document selector, embedded PDF viewer, and page navigation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  FileText,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Loader2,
  AlertCircle,
  ChevronDown,
  File,
  Calendar,
} from 'lucide-react';
import { documentService, ClientDocument } from '../../../services/documentService';
import { logger } from '../../../utils/logger';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker — Vite resolves the ?url import to the correct asset path
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface DocumentViewerPanelProps {
  clientId: string;
  documentIds?: string[];
}

const DocumentViewerPanel: React.FC<DocumentViewerPanelProps> = ({
  clientId,
  documentIds,
}) => {
  // Document list state
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // TASK-683: a citation links here as ?doc=<id>&page=<n>. Read once per navigation; the
  // page is applied after the PDF loads (below), because numPages is not known before then.
  const [searchParams] = useSearchParams();
  const deepLinkDocId = searchParams.get('doc');
  const deepLinkPage = Number(searchParams.get('page')) || null;

  // PDF viewer state
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(0.9);

  // Fetch document list
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoadingList(true);
        setListError(null);
        const response = await documentService.listDocuments({
          client_id: clientId,
        });
        const docs = response.documents || [];
        // Filter to provided IDs if specified
        const filtered =
          documentIds && documentIds.length > 0
            ? docs.filter((d) => documentIds.includes(d.id))
            : docs;
        setDocuments(filtered);
        // TASK-683: a provenance link arrives as ?doc=&page= — open THAT document, not the
        // newest one, or "click through to the source" lands on the wrong page of the wrong
        // file. An unknown id falls back to the normal auto-select.
        const requested = filtered.find((d) => d.id === deepLinkDocId);
        if (requested) {
          setSelectedDocId(requested.id);
        } else if (filtered.length > 0 && !selectedDocId) {
          setSelectedDocId(filtered[0].id);
        }
      } catch (err) {
        logger.error('[DocumentViewerPanel] Failed to load documents:', err);
        setListError('Failed to load documents');
      } finally {
        setLoadingList(false);
      }
    };

    fetchDocuments();
  }, [clientId, documentIds, deepLinkDocId]);

  // Fetch PDF URL when selected document changes
  useEffect(() => {
    if (!selectedDocId) {
      setPdfUrl(null);
      return;
    }

    const fetchPdfUrl = async () => {
      try {
        setLoadingPdf(true);
        setPdfError(null);
        setCurrentPage(1);
        setNumPages(0);

        const presigned = await documentService.getPresignedUrl(
          selectedDocId,
          3600
        );
        setPdfUrl(presigned.url);
      } catch (err) {
        logger.error('[DocumentViewerPanel] Failed to get PDF URL:', err);
        setPdfError('Failed to load document preview');
      } finally {
        setLoadingPdf(false);
      }
    };

    fetchPdfUrl();

    // Cleanup previous URL
    return () => {
      setPdfUrl(null);
    };
  }, [selectedDocId]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: pages }: { numPages: number }) => {
      setNumPages(pages);
      // TASK-683: land on the cited page when the link asked for one and it exists.
      const target =
        deepLinkDocId === selectedDocId && deepLinkPage && deepLinkPage <= pages
          ? deepLinkPage
          : 1;
      setCurrentPage(target);
    },
    [deepLinkDocId, deepLinkPage, selectedDocId]
  );

  const onDocumentLoadError = useCallback((error: Error) => {
    logger.error('[DocumentViewerPanel] PDF load error:', error);
    setPdfError('Failed to render PDF. The file may be corrupted.');
  }, []);

  const goToPrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const goToNextPage = () => setCurrentPage((p) => Math.min(numPages, p + 1));

  const zoomIn = () => setScale((s) => Math.min(2.0, s + 0.15));
  const zoomOut = () => setScale((s) => Math.max(0.4, s - 0.15));

  const selectedDoc = documents.find((d) => d.id === selectedDocId);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Document selector */}
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
        <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <div className="relative flex-1">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            disabled={loadingList}
            className="w-full flex items-center justify-between px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <span className="truncate">
              {loadingList
                ? 'Loading documents...'
                : selectedDoc
                ? selectedDoc.filename
                : 'Select a document'}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
          </button>

          {dropdownOpen && documents.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => {
                    setSelectedDocId(doc.id);
                    setDropdownOpen(false);
                  }}
                  className={`w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                    doc.id === selectedDocId ? 'bg-blue-50' : ''
                  }`}
                >
                  <File className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {doc.filename}
                    </div>
                    <div className="text-xs text-gray-500">
                      {documentService.getDocTypeDisplayName(doc.doc_type)}
                      {doc.file_size && (
                        <> - {documentService.formatFileSize(doc.file_size)}</>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Document metadata bar */}
      {selectedDoc && (
        <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-100 border-b border-gray-200 text-xs text-gray-500">
          <span>
            {documentService.getDocTypeDisplayName(selectedDoc.doc_type)}
          </span>
          {selectedDoc.file_size && (
            <>
              <span className="text-gray-300">|</span>
              <span>{documentService.formatFileSize(selectedDoc.file_size)}</span>
            </>
          )}
          {numPages > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span>{numPages} pages</span>
            </>
          )}
          {selectedDoc.created_at && (
            <>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(selectedDoc.created_at).toLocaleDateString()}
              </span>
            </>
          )}
        </div>
      )}

      {/* PDF Viewer area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* States: loading list, no docs, loading pdf, error, pdf */}
        {loadingList ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading documents...</p>
            </div>
          </div>
        ) : listError ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600">{listError}</p>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center px-4">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No documents uploaded yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Upload documents in the KYC form to view them here
              </p>
            </div>
          </div>
        ) : loadingPdf ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : pdfError ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-red-600">{pdfError}</p>
            </div>
          </div>
        ) : pdfUrl ? (
          <>
            {/* PDF content */}
            <div className="flex-1 overflow-auto flex justify-center bg-gray-200 p-2">
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="bg-white shadow-md"
                />
              </Document>
            </div>

            {/* Page navigation + zoom controls */}
            <div className="flex items-center justify-between px-3 py-2 bg-white border-t border-gray-200">
              <div className="flex items-center gap-1">
                <button
                  onClick={zoomOut}
                  disabled={scale <= 0.4}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                  title="Zoom out"
                >
                  <ZoomOut className="w-4 h-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-500 w-10 text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={zoomIn}
                  disabled={scale >= 2.0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                  title="Zoom in"
                >
                  <ZoomIn className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage <= 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                  title="Previous page"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-600 min-w-[60px] text-center">
                  {currentPage} / {numPages}
                </span>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage >= numPages}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                  title="Next page"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-gray-500">
              Select a document to preview
            </p>
          </div>
        )}
      </div>

      {/* Click-outside handler for dropdown */}
      {dropdownOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default DocumentViewerPanel;
